import { useMemo } from 'react'
import { Form, Input } from 'antd'
import { formatWithThousandSeparators, stripThousandSeparators, toNumericValue } from '../../../../utils/numberFormatter.js'

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Evaluate a column-level formula using sibling column values from the same row.
 * Prefers formulaHtml (uses stable column IDs) over formula (uses column labels
 * which can become stale if columns are renamed).
 */
export const evaluateColumnFormula = (formula, formulaHtml, allColumns, rowValues) => {
    if (!formula && !formulaHtml) return null

    const getColValue = (colId) => {
        let raw = rowValues?.[colId]
        // For dropdown columns the stored value may be a UUID — resolve to the
        // option label which holds the actual user-visible (numeric) value
        const col = (allColumns || []).find((c) => c?.id === colId)
        if (col && col.inputType === 'dropdown' && Array.isArray(col.options) && raw) {
            const opt = col.options.find((o) => String(o?.value) === String(raw))
            if (opt) raw = opt.label
        }
        return toNumericValue(raw)
    }

    let expression = null

    // Prefer formulaHtml as it uses stable column IDs via data-value attributes
    if (formulaHtml) {
        try {
            const parser = new DOMParser()
            const doc = parser.parseFromString(formulaHtml, 'text/html')
            const widgets = doc.querySelectorAll('.widget-inline-block')
            // Only use HTML-based evaluation if actual widget elements were found;
            // otherwise the raw text still contains @labels and should fall through
            // to the label-based formula path
            if (widgets.length > 0) {
                for (const elm of widgets) {
                    const colId = elm.getAttribute('data-value')
                    elm.replaceWith(String(getColValue(colId)))
                }
                const htmlExpression = (doc.body?.textContent || '').trim()
                if (htmlExpression) {
                    expression = htmlExpression
                }
            }
        } catch {
            // fall through to formula-based evaluation
        }
    }

    // Fall back to label-based formula if formulaHtml didn't produce a result
    if (!expression && formula) {
        expression = String(formula)
        const cols = (allColumns || [])
            .filter((col) => col?.label)
            .sort((a, b) => b.label.length - a.label.length)

        cols.forEach((col) => {
            const pattern = new RegExp(`@${escapeRegex(col.label)}`, 'g')
            expression = expression.replace(pattern, String(getColValue(col.id)))
        })
    }

    if (!expression) return null

    let str = expression.replace(/@[A-Za-z0-9_\-./\s]+/g, '0').replace(/@/g, '')
    const compact = str.replace(/\s+/g, '')
    if (!compact) return null
    if (/[^0-9+\-*/().]/.test(compact)) return null
    if (/[+\-*/.(]$/.test(compact)) return null

    try {
        // eslint-disable-next-line no-new-func
        const result = new Function('return ' + str)()
        const num = Number(result)
        return Number.isFinite(num) ? num : null
    } catch {
        return null
    }
}

const NumberForm = (props) => {
    const { values, disabled = false, parent = '', tableLabel = '', computedNumber, formula, formulaHtml, allColumns } = props
    const currentValue = values?.[props.id]

    // Compute display value from formula — purely for display, no side effects
    const computedValue = useMemo(() => {
        if (!computedNumber) return null
        return evaluateColumnFormula(formula, formulaHtml, allColumns, values)
    }, [computedNumber, formula, formulaHtml, allColumns, values])

    const isComputed = computedNumber && computedValue !== null
    const displayValue = isComputed ? computedValue : currentValue
    const formattedDisplay = formatWithThousandSeparators(displayValue)

    const handleNumberChange = (raw) => {
        const cleaned = stripThousandSeparators(raw).replace(/[^0-9.\-]/g, '')
        props.handleChange(props.id, cleaned, props.documentId)
    }

    return (
        <>
            {
                disabled ? <span>{formattedDisplay}</span>
                :
                isComputed ? (
                    <span style={{ display: 'inline-block', padding: '4px 11px', color: '#333', backgroundColor: '#f5f5f5', borderRadius: '6px', minWidth: '60px', textAlign: 'right' }}>
                        {formattedDisplay}
                    </span>
                ) : (
                    <Form.Item label="" preserve={false} style={{ marginBottom: 0 }}>
                        <Input
                            disabled={disabled}
                            min={0}
                            inputMode="decimal"
                            value={formatWithThousandSeparators(currentValue)}
                            onChange={(e) => handleNumberChange(e.target.value)}
                        />
                    </Form.Item>
                )
            }
        </>
    )
}

export default NumberForm
